import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Card, Button, IconButton } from '../components/ui';
import { AlertCircle, Edit, CheckCircle2, Clock, Zap, Eye } from 'lucide-react';
import { SummaryForm, WebsiteForm, OfferingsForm, FormWrapper } from '../components/Forms';
import StepperWrapper from '../components/Onboarding/StepperWrapper';
import Tabs from '../components/ui/Tabs';
import useMediaQuery from '../hooks/useMediaQuery';
import { useOnboardingMocked } from '../hooks/useOnboardingMocked';
import SocialsPage from './SocialsPage';
import PostPreview from '../components/PostModal/PostPreview';
import LoaderWithFacts from '../components/Loader/LoaderWithFacts';
import SmartphoneMockup from '../components/ui/SmartphoneMockup';
import RegeneratePostForm from '../components/PostModal/RegeneratePostForm';

const TestPage = () => {
  const { isMobile, isSmallMobile, isTablet } = useMediaQuery();
  const isMobileView = isMobile || isSmallMobile || isTablet;
  const [previewPlatform] = useState('instagram');
  const [activePreview, setActivePreview] = useState('post');

  // Caption editing state
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [editedCaption, setEditedCaption] = useState('');

  // Regenerate panel state
  const [showRegenerateForm, setShowRegenerateForm] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const steps = [
    { title: 'Enter Website' },
    { title: 'Site Brand' },
    { title: 'Generating Post' },
    { title: 'Connect Socials' },
  ];

  const {
    websiteUrlInput,
    setWebsiteUrlInput,
    websiteUrl,
    flowState,
    currentStep,
    profileError,
    handleWebsiteSubmit,
    handleRetry,
    handleStepChange,
    handleFinish,
    createProfileMutation,
    profileSummary,
    summaryLoading,
    summaryError,
    profileOfferings,
    offeringsLoading,
    offeringsError,
    handleSaveSummary,
    handleSaveOffering,
    handleDeleteOffering,
    handleAddOffering,
    updateSummaryMutation,
    updateOfferingMutation,
    firstPendingStoryPost,
    firstPendingFeedPost,
  } = useOnboardingMocked();

  const business_name = 'Demo Business';
  const logo = null;

  // Active post for the currently selected tab
  const activePost = activePreview === 'post' ? firstPendingFeedPost : firstPendingStoryPost;

  // Local caption state per post (mocked — no real API)
  const [captionOverrides, setCaptionOverrides] = useState({});
  const displayCaption = captionOverrides[activePost?.id] ?? activePost?.caption ?? '';

  const handleTabChange = useCallback((id) => {
    setActivePreview(id);
    setIsEditingCaption(false);
    setShowRegenerateForm(false);
  }, []);

  useEffect(() => {
    if (activePost?.caption && editedCaption === '') {
      setEditedCaption(activePost.caption);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePost?.id]);

  const handleCaptionSave = () => {
    if (!activePost?.id) return;
    setCaptionOverrides((prev) => ({ ...prev, [activePost.id]: editedCaption }));
    setIsEditingCaption(false);
  };

  const handleCaptionCancel = () => {
    setEditedCaption(displayCaption);
    setIsEditingCaption(false);
  };

  // Mocked regenerate — just shows a 1.5s spinner then closes
  const handleRegenerateSubmit = useCallback(async (_userInput) => {
    setIsRegenerating(true);
    await new Promise((r) => setTimeout(r, 1500));
    setIsRegenerating(false);
    setShowRegenerateForm(false);
  }, []);

  const shouldShowPostsPreview = useMemo(() => {
    return (
      firstPendingFeedPost?.status === 'pending' || firstPendingStoryPost?.status === 'pending'
    );
  }, [firstPendingFeedPost, firstPendingStoryPost]);

  // Show error state with retry button
  if (flowState === 'failed') {
    return (
      <div className='min-h-[100vh] bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-[20%]'>
        <Card>
          <Card.Body>
            <div className='bg-red-50 border border-red-200 rounded-md p-4'>
              <div className='flex items-center space-x-2'>
                <AlertCircle className='w-5 h-5 text-red-600' />
                <span className='text-red-800 font-medium'>Profile Creation Failed</span>
              </div>
              <p className='text-red-700 text-sm mt-1'>
                {profileError || 'Failed to create profile. Please try again.'}
              </p>
              <div className='mt-3 text-sm text-gray-600'>
                <p>
                  Website URL: <span className='font-medium'>{websiteUrl}</span>
                </p>
              </div>
              <Button
                variant='primary'
                size='sm'
                className='mt-3'
                onClick={handleRetry}
                disabled={createProfileMutation.isPending}
              >
                {createProfileMutation.isPending ? 'Retrying...' : 'Try Again'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen overflow-y-auto flex flex-col justify-start items-center py-5 px-4 onboarding-bg'>
      <div className='w-full max-w-[1600px] flex flex-col flex-1 md:px-12'>
        <StepperWrapper
          className='flex flex-col flex-1'
          steps={steps}
          hideButtons={(stepIndex) => stepIndex === 0}
          hideBackButton={(stepIndex) => stepIndex === 1}
          initialStep={currentStep}
          onStepChange={handleStepChange}
          onFinish={handleFinish}
          canProceed={(stepIndex) => {
            if (stepIndex === 0) return flowState === 'profile_ready';
            if (stepIndex === 1) return !summaryLoading && !offeringsLoading;
            if (stepIndex === 2) {
              return (
                firstPendingStoryPost?.status === 'pending' ||
                firstPendingFeedPost?.status === 'pending'
              );
            }
            return true;
          }}
          stepsWrapperClassName='p-0 flex flex-col flex-1'
          disableBackButton={(stepIndex) => stepIndex === 1}
        >
          {/* Step 0: Enter Website */}
          <WebsiteForm
            error={profileError}
            loading={createProfileMutation.isPending}
            websiteUrlInput={websiteUrlInput}
            setWebsiteUrlInput={setWebsiteUrlInput}
            onSubmit={handleWebsiteSubmit}
          />

          {/* Step 1: Site Brand */}
          {flowState === 'analyzing' ? (
            <LoaderWithFacts
              type='brand_dna'
              title='Building your Brand DNA - This may up to 1 minute'
            />
          ) : (
            <>
              {isMobileView && (
                <Tabs
                  className='flex-1 min-h-0 bg-transparent p-0'
                  tabs={[
                    { id: 'summary', title: 'Summary' },
                    { id: 'offerings', title: 'Offerings' },
                  ]}
                  initialActiveId={'summary'}
                >
                  <FormWrapper
                    isLoading={summaryLoading}
                    error={summaryError}
                    data={profileSummary}
                    errorMessage='Failed to load summary data'
                    tabId='summary'
                    className='h-full'
                  >
                    <SummaryForm
                      initialData={profileSummary}
                      onSave={handleSaveSummary}
                      isLoading={updateSummaryMutation.isPending}
                    />
                  </FormWrapper>

                  <FormWrapper
                    isLoading={offeringsLoading}
                    error={offeringsError}
                    data={profileOfferings}
                    errorMessage='Failed to load offerings data'
                    tabId='offerings'
                    className='h-full'
                  >
                    <OfferingsForm
                      initialData={profileOfferings || []}
                      onSave={handleSaveOffering}
                      onDelete={handleDeleteOffering}
                      isLoading={updateOfferingMutation.isPending}
                      className='pl-0'
                      onAdd={handleAddOffering}
                    />
                  </FormWrapper>
                </Tabs>
              )}

              {!isMobileView && (
                <div className='w-full h-full flex-1 min-h-0 xs:hidden md:grid md:grid-cols-2 gap-6 p-3 border-white/20'>
                  <FormWrapper
                    isLoading={summaryLoading}
                    error={summaryError}
                    data={profileSummary}
                    errorMessage='Failed to load summary data'
                    className='h-full overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300'
                  >
                    <SummaryForm
                      initialData={profileSummary}
                      onSave={handleSaveSummary}
                      isLoading={updateSummaryMutation.isPending}
                    />
                  </FormWrapper>

                  <FormWrapper
                    isLoading={offeringsLoading}
                    error={offeringsError}
                    data={profileOfferings}
                    errorMessage='Failed to load offerings data'
                    className='h-full overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300'
                  >
                    <OfferingsForm
                      initialData={profileOfferings || []}
                      onSave={handleSaveOffering}
                      onDelete={handleDeleteOffering}
                      isLoading={updateOfferingMutation.isPending}
                      onAdd={handleAddOffering}
                    />
                  </FormWrapper>
                </div>
              )}
            </>
          )}

          {/* Step 2: Generating Post — split-screen layout */}
          <div
            className='relative flex flex-col flex-1 mt-4 md:mt-0 rounded-xl overflow-hidden'
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              boxShadow: '0 4px 32px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
            }}
          >
            {!shouldShowPostsPreview ? (
              <div className='flex flex-1 items-center justify-center p-4 md:p-6'>
                <LoaderWithFacts
                  type='social_media_posts'
                  title='Generating your first post - This may take up to 1 minute'
                />
              </div>
            ) : (
              <div
                className='grid flex-1 min-h-0 grid-cols-1 lg:grid-cols-[38%_62%] divide-y lg:divide-y-0 lg:divide-x'
                style={{ borderColor: 'var(--border)' }}
              >
                {/* ── LEFT: Controls — desktop only ───────────────────────── */}
                <div className='hidden lg:flex flex-col gap-5 p-5 overflow-y-auto thin-scrollbar order-2 lg:order-1'>

                  {/* Post / Story toggle */}
                  <div>
                    <p className='mb-1.5 text-xs font-semibold uppercase tracking-wide' style={{ color: 'var(--text-faint)' }}>
                      Format
                    </p>
                    <Tabs
                      className='bg-transparent p-0 gap-0'
                      tabs={[
                        { id: 'post', title: 'Post' },
                        { id: 'story', title: 'Story' },
                      ]}
                      activeId={activePreview}
                      onChange={handleTabChange}
                      initialActiveId='post'
                    >
                      <div tabId='post' />
                      <div tabId='story' />
                    </Tabs>
                  </div>

                  {showRegenerateForm ? (
                    /* ── Regenerate form ── */
                    activePost?.id ? (
                      <RegeneratePostForm
                        post={{ ...activePost, status: isRegenerating ? 'regeneration' : 'pending' }}
                        onRegenerateSubmit={handleRegenerateSubmit}
                        onCancel={() => setShowRegenerateForm(false)}
                      />
                    ) : null
                  ) : (
                    <>
                      {/* Generated caption — feed only; stories have no caption */}
                      {activePreview === 'post' && (
                        <div className='flex flex-col gap-2'>
                          <div className='flex items-center justify-between'>
                            <p className='text-xs font-semibold uppercase tracking-wide' style={{ color: 'var(--text-faint)' }}>
                              Generated Caption
                            </p>
                            {!isEditingCaption && (
                              <IconButton
                                icon={Edit}
                                onClick={() => {
                                  setEditedCaption(displayCaption);
                                  setIsEditingCaption(true);
                                }}
                                variant='ghost'
                                size='sm'
                                tooltip='Edit caption'
                              />
                            )}
                          </div>

                          {isEditingCaption ? (
                            <div className='flex flex-col gap-2'>
                              <textarea
                                value={editedCaption}
                                onChange={(e) => setEditedCaption(e.target.value)}
                                rows={6}
                                className='w-full resize-none rounded-xl p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 transition-colors'
                                style={{
                                  background: 'var(--bg-surface)',
                                  border: '1px solid var(--border)',
                                  color: 'var(--text-body)',
                                }}
                                placeholder='Enter caption...'
                              />
                              <div className='flex items-center gap-2'>
                                <Button
                                  variant='primary'
                                  size='sm'
                                  onClick={handleCaptionSave}
                                  disabled={editedCaption === displayCaption}
                                >
                                  Save
                                </Button>
                                <Button variant='neutral' size='sm' onClick={handleCaptionCancel}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p
                              className='w-full rounded-xl p-3 text-sm leading-relaxed'
                              style={{
                                background: 'var(--bg-surface)',
                                border: '1px solid var(--border)',
                                color: 'var(--text-body)',
                                minHeight: '7rem',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {displayCaption || 'Loading caption…'}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Story tips — shown when Story tab is active */}
                      {activePreview === 'story' && (
                        <div className='flex flex-col gap-3'>
                          <p className='text-xs font-semibold uppercase tracking-wide' style={{ color: 'var(--text-faint)' }}>
                            Story tips
                          </p>
                          <div
                            className='flex flex-col gap-0 rounded-xl overflow-hidden'
                            style={{ border: '1px solid var(--border)' }}
                          >
                            {[
                              { icon: Eye,          text: 'Stories disappear after 24 hours — great for time-sensitive offers and flash sales.' },
                              { icon: Zap,          text: 'Full-screen vertical format grabs attention instantly in the feed.' },
                              { icon: Clock,        text: 'Post between 7–9 AM or 6–9 PM for the highest story views.' },
                              { icon: CheckCircle2, text: 'Consistency matters — showing up daily builds brand recall fast.' },
                            ].map(({ icon: Icon, text }, i, arr) => (
                              <div
                                key={i}
                                className='flex items-start gap-3 px-4 py-3'
                                style={{
                                  background: 'var(--bg-surface)',
                                  borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                                }}
                              >
                                <Icon
                                  className='w-4 h-4 mt-0.5 flex-shrink-0'
                                  style={{ color: 'var(--brand)' }}
                                />
                                <p className='text-xs leading-relaxed m-0' style={{ color: 'var(--text-body)' }}>
                                  {text}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* "Don't like this post?" — only when post is ready */}
                      {activePost?.id && (
                        <button
                          type='button'
                          onClick={() => {
                            setIsEditingCaption(false);
                            setShowRegenerateForm(true);
                          }}
                          className='self-start text-xs transition-colors cursor-pointer'
                          style={{ color: 'var(--text-faint)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--brand)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                        >
                          Don't like this post?
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* ── RIGHT: Smartphone preview ────────────────────────────── */}
                <div
                  className='relative flex flex-col items-center order-1 lg:order-2 min-h-[320px] lg:min-h-0 overflow-hidden'
                  style={{ background: 'var(--bg-surface)' }}
                >
                  {/* Mobile-only tab bar above the preview */}
                  <div
                    className='flex lg:hidden w-full border-b'
                    style={{
                      background: 'var(--bg-card)',
                      borderColor: 'var(--border)',
                    }}
                  >
                    {['post', 'story'].map((type) => (
                      <button
                        key={type}
                        type='button'
                        onClick={() => setActivePreview(type)}
                        className='flex-1 py-2.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px cursor-pointer'
                        style={{
                          borderBottomColor: activePreview === type ? 'var(--brand)' : 'transparent',
                          color: activePreview === type ? 'var(--brand)' : 'var(--text-muted)',
                        }}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  {/* Preview content */}
                  <div className='flex flex-1 items-center justify-center p-4 lg:p-6 w-full overflow-hidden'>
                    {activePreview === 'story' ? (
                      <SmartphoneMockup>
                        <PostPreview
                          post={firstPendingStoryPost}
                          layout='story'
                          businessName={business_name}
                          loading={firstPendingStoryPost?.status !== 'pending'}
                          logo={logo}
                          platform={previewPlatform}
                        />
                      </SmartphoneMockup>
                    ) : (
                      <div className='w-full max-w-[380px]'>
                        <PostPreview
                          post={firstPendingFeedPost}
                          layout='feed'
                          businessName={business_name}
                          logo={logo}
                          loading={firstPendingFeedPost?.status !== 'pending'}
                          platform={previewPlatform}
                        />
                      </div>
                    )}
                  </div>

                  {/* Mobile-only "Don't like this post?" link — only when post is ready */}
                  {activePost?.id && (
                    <div className='flex lg:hidden w-full justify-center py-3'>
                      <button
                        type='button'
                        onClick={() => {
                          setIsEditingCaption(false);
                          setShowRegenerateForm(true);
                        }}
                        className='text-xs transition-colors cursor-pointer'
                        style={{ color: 'var(--text-faint)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--brand)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                      >
                        Don't like this post?
                      </button>
                    </div>
                  )}

                  {/* Mobile regenerate overlay — slides up from the bottom */}
                  {showRegenerateForm && activePost?.id && (
                    <div
                      className='lg:hidden absolute inset-0 z-10 flex flex-col justify-end'
                      style={{ background: 'rgba(0,0,0,0.45)' }}
                      onClick={(e) => { if (e.target === e.currentTarget) setShowRegenerateForm(false); }}
                    >
                      <div
                        className='flex flex-col gap-4 p-5 rounded-t-2xl max-h-[80%] overflow-y-auto'
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        <RegeneratePostForm
                          post={{ ...activePost, status: isRegenerating ? 'regeneration' : 'pending' }}
                          onRegenerateSubmit={handleRegenerateSubmit}
                          onCancel={() => setShowRegenerateForm(false)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Connect Socials */}
          <div className='flex-1 min-h-0 overflow-y-auto pt-2 md:pt-4'>
            <SocialsPage isOnboarding currentStep={currentStep} onFinish={handleFinish} />
          </div>
        </StepperWrapper>
      </div>
    </div>
  );
};

export default TestPage;
